import { __read } from "tslib";
import { useFeatureGates } from '@office-start/feature-gates-service';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState, } from 'react';
import { HarmonySuspenseContext } from './HarmonySuspenseContext';
import { getDeferredPromise } from './utils';
function createDummyLazyComponent(loadPromise) {
    return lazy(function () {
        return loadPromise.then(function () { return ({
            default: function () { return null; },
        }); });
    });
}
/**
 * HarmonySuspender is a component with no ui that will suspend until isLoading is set to false
 *
 * It accomplishes this by passing a deferred promise that resolves to a "dummy" component
 * to React.lazy.
 *
 * While the deferred promise is pending, React.lazy will cause he parent <Suspend>
 * to suspend and show the fallback.
 *
 * Once isLoading is set to true we resolve the Promise and the dummy component will
 * render causing the parent <Suspense> to stop suspending and show the content
 * @param isLoading If true we will render a dummy lazy component that will suspend until isLoading is false
 * @returns  null
 */
var HarmonySuspender = function (_a) {
    var isLoading = _a.isLoading;
    var loadPromise = useMemo(function () { return getDeferredPromise(); }, []);
    var Suspender = useMemo(function () { return createDummyLazyComponent(loadPromise.promise); }, [loadPromise]);
    useEffect(function () {
        if (!isLoading) {
            loadPromise.resolve();
        }
    }, [isLoading, loadPromise]);
    return React.createElement(Suspender, null);
};
var HarmonySuspenseContainer = function (_a) {
    var useContainerStyles = _a.useContainerStyles, children = _a.children;
    var styles = useContainerStyles === null || useContainerStyles === void 0 ? void 0 : useContainerStyles();
    return styles ? (React.createElement("div", { className: styles === null || styles === void 0 ? void 0 : styles.container }, children)) : (React.createElement(React.Fragment, null, children));
};
/**
 * HarmonySuspense is a wrapper around React.Suspense that gives us more flexibility
 * in rendering a fallback while we are waiting for a component/data to load.
 *
 * The suspendUntilReady prop is set to true we will suspend until the callback
 * returned from useMarkLoadComplete is called from a child component. This allows us to wait
 * until the child components have actually finished rendering before removing the fallback.
 *
 * When suspendUntilReady is false this component functions exactly like React.Suspense
 * @returns
 */
export var HarmonySuspense = function (_a) {
    var _b = _a.fallback, _c = _b === void 0 ? {
        node: null,
    } : _b, fallbackNode = _c.node, useContainerStyles = _c.useContainerStyles, children = _a.children, _d = _a.suspendUntilReady, suspendUntilReady = _d === void 0 ? false : _d, _e = _a.canLoadChildren, canLoadChildren = _e === void 0 ? true : _e;
    var featureGates = useFeatureGates().data;
    var _f = __read(useState(fallbackNode !== null), 2), isLoading = _f[0], setLoading = _f[1];
    var markLoadComplete = useCallback(function () {
        setLoading(false);
    }, [setLoading]);
    var shouldUseSuspender = useMemo(function () {
        var _a, _b;
        return (suspendUntilReady &&
            !!((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.loadingUiGates) === null || _b === void 0 ? void 0 : _b.loadingUiEnabled));
    }, [suspendUntilReady, featureGates]);
    return (React.createElement(HarmonySuspenseContext.Provider, { value: { markLoadComplete: markLoadComplete } },
        React.createElement(HarmonySuspenseContainer, { useContainerStyles: useContainerStyles },
            React.createElement(Suspense, { fallback: fallbackNode },
                shouldUseSuspender ? (React.createElement(HarmonySuspender, { isLoading: isLoading })) : null,
                canLoadChildren ? children : null))));
};
//# sourceMappingURL=HarmonySuspense.js.map